import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import {
  IconTooltipDriver,
  iconTooltipDriverFactory,
} from '../IconTooltip/IconTooltip.driver';
import { DATA_ATTRS, DATA_HOOKS } from './dataKeys';
import { fieldsetDriverFactory } from '../internal/Fieldset/Fieldset.driver';
import { dataHook } from '../../testkitUtils/utils';

export interface CounterDriver extends BaseUniDriver {
  errorIcon: IconTooltipDriver;
  enterValue(val: string): Promise<void>;
  getCounterAriaLabel(): Promise<string>;
  getCounterAriaLabellledby(): Promise<string>;
  getErrorMessageContent(): Promise<string>;
  hasCounterComponentError(): Promise<boolean>;
  isCounterComponentDisabled(): Promise<boolean>;
  isErrorTooltipExists(): Promise<boolean>;
  isErrorTooltipShown(): Promise<boolean>;
  isInputComponentDisabled(): Promise<boolean>;
  isInputValueEqualTo(val: number): Promise<boolean>;
  isMinusButtonDisabled(): Promise<boolean>;
  isPlusButtonDisabled(): Promise<boolean>;
  pressMinus(): Promise<void>;
  pressPlus(): Promise<void>;
  clickOnErrorIcon(): Promise<void>;
  click(): Promise<void>;
  getInputAriaDescribedBy(): Promise<string>;
  getAriaValueMax(): Promise<number>;
  getAriaValueMin(): Promise<number>;
  getAriaValueNow(): Promise<number>;
  getAriaInvalid(): Promise<boolean>;
  getLabel(): UniDriver;
  getLabelAsterisk(): UniDriver;
  isFocused(): Promise<boolean>;
}

export const counterDriverFactory = (
  base: UniDriver,
  body: UniDriver,
): CounterDriver => {
  const iconTooltipDriver = iconTooltipDriverFactory(
    base.$(dataHook(DATA_HOOKS.ERROR_TOOLTIP_ICON)),
    body,
  );

  const getFieldSetDriver = () => fieldsetDriverFactory(base);

  const getPlusButton = () => base.$(dataHook(DATA_HOOKS.PLUS_BUTTON));
  const getMinusButton = () => base.$(dataHook(DATA_HOOKS.MINUS_BUTTON));
  const getInput = () => base.$('input');
  const getAriaLabel = (label: string) => base.attr(`aria-${label}`);

  return {
    ...baseUniDriverFactory(base),
    errorIcon: iconTooltipDriver,
    isCounterComponentDisabled: async () =>
      (await base.attr(DATA_ATTRS.DISABLED)) === 'true',
    isInputComponentDisabled: async () => !!getInput()._prop('disabled'),
    hasCounterComponentError: async () =>
      (await base.attr(DATA_ATTRS.ERROR)) === 'true',
    isMinusButtonDisabled: async () =>
      !!(await getMinusButton()._prop('disabled')),
    isPlusButtonDisabled: async () =>
      !!(await getPlusButton()._prop('disabled')),
    getErrorMessageContent: async () => {
      const element = iconTooltipDriver.getTooltipContent();

      return element.text();
    },
    isInputValueEqualTo: async (val: number) =>
      (await getInput().value()) === val.toString(),
    pressMinus: async () => getMinusButton().click(),
    pressPlus: async () => getPlusButton().click(),
    getCounterAriaLabel: async () => getAriaLabel('label'),
    getCounterAriaLabellledby: async () => getAriaLabel('labelledby'),
    isErrorTooltipExists: async () => {
      return iconTooltipDriver.exists();
    },
    isErrorTooltipShown: async () => {
      return iconTooltipDriver.tooltipExists();
    },
    enterValue: async (val: string) => {
      return getInput().enterValue(val);
    },
    click: async () => {
      return getInput().click();
    },

    clickOnErrorIcon: async () => {
      return iconTooltipDriver.click();
    },
    getInputAriaDescribedBy: async () => {
      return getInput().attr('aria-describedby');
    },
    getAriaValueMax: async () => {
      return Number(await getInput().attr('aria-valuemax'));
    },
    getAriaValueMin: async () => {
      return Number(await getInput().attr('aria-valuemin'));
    },
    getAriaValueNow: async () => {
      return Number(await getInput().attr('aria-valuenow'));
    },
    getAriaInvalid: async () => {
      return (await getInput().attr('aria-invalid')) === 'true';
    },
    getLabel: () => getFieldSetDriver().getLabel(),
    getLabelAsterisk: () => getFieldSetDriver().getLabelAsterisk(),
    isFocused: async () => (await base.attr(DATA_ATTRS.FOCUSED)) === 'true',
  };
};
